// web/src/components/Container.tsx
// Layout primitives: Container, Section, Bleed.
//
// Container: caps readable width at --col-max with consistent gutters.
// Section:   vertical-rhythm wrapper between major content blocks.
// Bleed:     breaks out of the Container to span the full viewport width —
//            used by hero blocks so a photo / dark band reads as full-bleed
//            magazine art.

import type { ReactNode, CSSProperties } from 'react';

interface ContainerProps {
  children: ReactNode;
  /** 'narrow' = --col-max (560px), 'wide' = --col-wide (920px). */
  width?: 'narrow' | 'wide';
  style?: CSSProperties;
}

export function Container({ children, width = 'narrow', style }: ContainerProps) {
  return (
    <div
      style={{
        maxWidth: width === 'wide' ? 'var(--col-wide)' : 'var(--col-max)',
        margin: '0 auto',
        padding: '0 var(--space-lg)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

interface SectionProps {
  children: ReactNode;
  /** Vertical gap between this section and the next. */
  spacing?: 'sm' | 'md' | 'lg' | 'xl';
  style?: CSSProperties;
}

const SECTION_GAP: Record<NonNullable<SectionProps['spacing']>, string> = {
  sm: 'var(--space-base)',
  md: 'var(--space-lg)',
  lg: 'var(--space-xl)',
  xl: 'var(--space-xxl)',
};

export function Section({ children, spacing = 'lg', style }: SectionProps) {
  return (
    <section style={{ marginBottom: SECTION_GAP[spacing], ...style }}>
      {children}
    </section>
  );
}

interface BleedProps {
  children: ReactNode;
  /** Background colour or image to fill the bled area. */
  background?: string;
  style?: CSSProperties;
}

/**
 * Escapes the parent Container to span 100vw via symmetric negative margins.
 * Works inside any container that uses the standard 24px gutter.
 */
export function Bleed({ children, background, style }: BleedProps) {
  return (
    <div
      style={{
        marginLeft: 'var(--bleed-escape)',
        marginRight: 'var(--bleed-escape)',
        background,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
